/**
 * useReducedMotion — observes prefers-reduced-motion media query.
 *
 * Returns `true` when the user has reduced-motion enabled. Updates
 * reactively if the OS-level setting changes during the session.
 *
 * Use to gate decorative animations / grain overlays / parallax —
 * functional motion (state transitions, button feedback) should
 * remain enabled even in reduced-motion mode.
 */
import { useEffect, useState } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(QUERY);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    // Fallback for ancient browsers — Safari < 14, etc.
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  return reduced;
}
